import { createContext, useContext } from "react";
import type { ExitRef } from "../exit-slots";
import type { ConnectCheck, RefusalReason } from "./connector-rules";
import type { DragOrigin, PointerBindings } from "./use-connector-drag";

/** How a tile looks while a connector is dragged: a place it can end (`over`: right under the pointer) or one it cannot, with why. */
export type DropView = { kind: "valid"; over: boolean } | { kind: "invalid"; reason: RefusalReason };

/** What the exit handles and the connection menu need from the canvas; one object so the tiles pass nothing down by hand. */
export interface ConnectorApi {
  /** Whether the author may change connections (a viewer only sees them and can still jump to a path). */
  editable: boolean;
  pathIds: readonly string[];
  hasPath: (id: string) => boolean;
  /** "Path D · Tour". */
  pathTitle: (id: string) => string;
  /** "D · Tour": the short form, for the chip on an exit row. */
  pathChip: (id: string) => string;
  /** The name of an exit as the author sees it ("Book a tour", "Else", "Booked"). */
  exitName: (ref: ExitRef) => string;
  selectedEdge: string | null;
  menuFor: string | null;
  /** The tile "Go to" just reached, which carries a ring for a moment. */
  highlight: string | null;
  dropView: (tileId: string) => DropView | null;
  check: (ref: ExitRef, tileId: string | null) => ConnectCheck;
  bind: (ref: ExitRef, origin: DragOrigin) => PointerBindings;
  takeSwallowedClick: () => boolean;
  select: (edgeId: string | null) => void;
  focus: (edgeId: string | null) => void;
  openMenu: (edgeId: string | null) => void;
  connect: (ref: ExitRef, target: string) => void;
  disconnect: (ref: ExitRef) => void;
  createPath: (ref: ExitRef) => void;
  goTo: (tileId: string) => void;
}

export const ConnectorContext = createContext<ConnectorApi | null>(null);

export function useConnectors(): ConnectorApi {
  const api = useContext(ConnectorContext);
  if (!api) throw new Error("Connector handles belong inside the canvas.");
  return api;
}
